import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { theme } from '../Theming/Theming';
import React, { useContext } from 'react';
import { styled } from 'linaria/react';
import ProductDetails from './ProductDetails';
import ProductTabs from './ProductTabs/ProductTabs';
import useCustomData from './useCustomData';
import MaxWidth from '../Layout/MaxWidth';
import { useVariantFromUrl } from '@jetshop/core/hooks/useProductVariants/useVariantFromUrl';
import ProductRow from '../ui/ProductRow/ProductRow';
import ImageSlider from './ImageSlider/ImageSlider';
import SiteContentContext from '../SiteContent/SiteContentContext';
import { CategoriesContent } from './CategoriesContent';
import { usePrimaryBlogPath } from '../Blog/hook/usePrimaryBlogPath';
import { LatestProducts } from './LatestProducts/LatestProducts';
import RelatedProducts from './RelatedProducts/RelatedProducts';
import { KlaviyoTrackViewedItem } from '../Klaviyo';
import ChannelContext from '@jetshop/core/components/ChannelContext';

const ProductPageContainer = styled(MaxWidth)`
  display: flex;
  align-items: center;
  flex-direction: column;
  ${theme.above.md} {
    padding-top: 1rem;
  }
  padding-bottom: 1rem;
`;

const ProductContainer = styled('div')`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  justify-content: space-between;
`;

export const Section = styled('section')`
  width: 100%;
  ${theme.only.md} {
    padding-bottom: 1rem;
    &.product-images-wrapper {
      width: 45%;
    }
    &.product-info-wrapper {
      width: 50%;
    }
  }
  ${theme.above.lg} {
    padding-bottom: 2rem;
    &.product-images-wrapper {
      width: 48%;
    }
    &.product-info-wrapper {
      width: 48%;
    }
  }
  ${theme.below.sm} {
    &.product-images-wrapper {
      margin-left: -${theme.general.pagePadding.sm};
      width: 100vw;
      .main-slider {
        width: 100vw;
      }
    }
  }
`;

export const SectionHeading = styled('h2')`
  font-size: 1.2rem;
  margin-bottom: 1rem;
`;

const ProductTabsWrapper = styled('div')`
  margin-top: 1rem;
  width: 100%;
  ${theme.above.md} {
    //width: 50%;
  }
`;

const ProductRowWrapper = styled('div')`
  margin: 3rem 0 3rem;
`;

const Product = ({ result: { data, loading }, product, partialProduct }) => {
  //PARTIAL PRODUCT FORM CATEGORY
  if (!product) {
    product = partialProduct;
  }

  //TRANSLATIONS
  const {
    relatedTitle,
    relatedSubTitle,
    bestSellingSubTitle,
    bestSellingTitle,
    blogTitle,
    latestTitle,
  } = useContext(SiteContentContext);

  //BLOG PATH
  const blogPath = usePrimaryBlogPath();

  const initialVariant = useVariantFromUrl();

  //GET VARIANTS HANDLING
  const variantHandler = useProductVariants(product, {
    initialVariant,
    preselectCheapest: false,
  });

  //CUSTOM DATA TO BE USED ELSEWHERE
  const { customFields, customTabs, customData } = useCustomData({ product });

  const { selectedChannel } = useContext(ChannelContext);

  //NULL CHECK
  if (!product) {
    return null;
  }

  //VARIANTS VARIABLES
  const { selectedVariant: selectedVariation } = variantHandler;

  //CUSTOM FIX TO SHOW VARIANT IMAGE WHEN JUST COLOR IS SELECTED
  const variantImageSlider =
    selectedVariation ||
    (product?.variants?.options?.length > 1 &&
      variantHandler.missingOptions?.length === 1 &&
      variantHandler.getVariantForSelection(
        variantHandler.missingOptions[0].values[0],
        variantHandler.missingOptions[0]
      ));

  //BREADCRUMBS
  const parents = getParentOrCanonicalRoute(data?.route?.parents, product);
  const breadcrumbProps = {
    breadcrumbs: data?.route?.breadcrumbs,
    parents,
  };

  //PARENT ID
  const path = data?.route?.path;
  let parentCategory = false;

  //PARENT CATEGORY
  if (product.categories && product.categories.length > 0) {
    parentCategory = product.categories[0];

    if (path?.search(blogPath) === -1) {
      const parentCartegories = product.categories.filter(
        (cat) => path.search(cat.primaryRoute.path) !== -1
      );
      parentCategory = parentCartegories[parentCartegories.length - 1];
    }
  }
  const parentId = parentCategory?.id;
  const hasRelated = product && product.relatedProducts?.length > 0;

  return (
    <>
      <ProductPageContainer>
        <KlaviyoTrackViewedItem
          product={product}
          productId={product.id}
          selectedChannel={selectedChannel}
        />
        <ProductContainer>
          <Section className="product-images-wrapper">
            <ImageSlider
              product={product}
              customData={customData}
              selectedVariation={variantImageSlider}
            />
          </Section>
          <Section className="product-info-wrapper">
            <ProductDetails
              {...{
                product,
                selectedVariation,
                variantHandler,
                breadcrumbProps,
              }}
            />
            <ProductTabsWrapper>
              <ProductTabs
                product={product}
                customFields={customFields}
                customTabs={customTabs}
                loading={loading}
              />
            </ProductTabsWrapper>
          </Section>
        </ProductContainer>
      </ProductPageContainer>
      {hasRelated && (
        <RelatedProducts
          products={product.relatedProducts}
          title={relatedTitle}
          subTitle={relatedSubTitle}
        />
      )}
      <CategoriesContent
        categories={product.categories}
        title={blogTitle}
        blogPath={blogPath}
      />

      {parentId && (
        <ProductRowWrapper>
          <ProductRow
            excludeId={product.id}
            id={parentId}
            slider={true}
            title={bestSellingTitle}
            subTitle={bestSellingSubTitle}
            orderBy="BESTSELLER"
          />
        </ProductRowWrapper>
      )}
      <LatestProducts product={product} title={latestTitle} />
    </>
  );
};

export default Product;
